import clsx from 'clsx';
import { connect } from 'react-redux';
import { useTheme } from '@rossum/ui/material';
import { organizationBranding } from '../../features/client-config/organization/branding';
import { safeOrganizationSelector } from '../../redux/modules/organization/selectors';
import { State } from '../../types/state';
import styles from './style.module.sass';

const LogoAnimate = ({ size }: { size: number }) => {
  const theme = useTheme();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      version="1.1"
      width={size}
      height={size}
    >
      <defs>
        <linearGradient
          id="logoAnimate-linear"
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop stopColor={theme.palette.primary.main} offset="0%" />
          <stop stopColor={theme.palette.primary.light} offset="100%" />
        </linearGradient>
      </defs>
      <g transform="translate(50, 50)">
        <circle
          cx="0"
          cy="0"
          r="33.5671"
          fill="none"
          stroke="url(#logoAnimate-linear)"
          strokeWidth="3"
        >
          <animateTransform
            attributeType="xml"
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            begin="0"
            dur="1s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.5;0.7;0.5"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="2"
          cy="4"
          r="33.5671"
          fill="none"
          stroke="url(#logoAnimate-linear)"
          strokeWidth="1"
        >
          <animateTransform
            attributeType="xml"
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            begin="0"
            dur="1.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.2;0.3;0.2"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="-2"
          cy="-4"
          r="33.5671"
          fill="none"
          stroke="url(#logoAnimate-linear)"
          strokeWidth="2"
        >
          <animateTransform
            attributeType="xml"
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            begin="0"
            dur="3s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.1;0.3;0.1"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
      </g>
    </svg>
  );
};

type StateProps = {
  loader: string | null;
};

type OwnProps = {
  absolute?: boolean;
  size?: number;
  className?: string;
};

type Props = StateProps & OwnProps;

const shouldAnimate = (loader: string) => {
  try {
    const base64 = loader.replace('data:image/svg+xml;base64,', '');
    return !window.atob(base64).includes('<animate');
  } catch {
    return false;
  }
};

const Loader = ({ loader, absolute = false, size = 150, className }: Props) => (
  <div
    id="loader"
    className={clsx(
      styles['Loader'],
      absolute && styles['absolute'],
      className
    )}
  >
    {loader ? (
      <img src={loader} alt="loader" width={size} height={size} />
    ) : (
      <LogoAnimate size={size} />
    )}
    {loader &&
      shouldAnimate(loader) &&
      (['StaticCircle', 'InnerCircle', 'OuterCircle'] as const).map(circle => (
        <div
          key={circle}
          style={{ borderRadius: size, width: size, height: size }}
          className={styles[circle]}
        />
      ))}
  </div>
);

const mapStateToProps = (state: State): StateProps => {
  const organization = safeOrganizationSelector(state);
  if (!organization) {
    return {
      loader: null,
    };
  }
  return {
    loader: organizationBranding.customLoaderImage(organization.uiSettings),
  };
};

export default connect(mapStateToProps)(Loader);
